import React from "react";
import { ReportingProvider } from "@veiligh/reporting-core/ui";

import { WebReportingAdapter } from "~/adapters/reporting-adapter";
import { WebReportingAuth } from "~/adapters/reporting-auth";
import { DEFAULT_API_PAYLOAD } from "~/lib/constants";
import { restApi } from "~/redux/restApi";
import { useSelector } from "~/redux/store";

type SidebarPermissionNode = {
  key?: string | null;
  requiredPermissionKey?: string[] | null;
  children?: SidebarPermissionNode[] | null;
};

const collectPermissionKeysFromSections = (sections: unknown[]): string[] => {
  const keys = new Set<string>();

  sections.forEach((section) => {
    if (!section || typeof section !== "object") {
      return;
    }

    const children = (section as { children?: unknown[] }).children;
    if (!Array.isArray(children)) {
      return;
    }

    children.forEach((child) => {
      if (!child || typeof child !== "object") {
        return;
      }

      const key = (child as { key?: string | null }).key;
      if (typeof key === "string" && key.trim()) {
        keys.add(key);
      }
    });
  });

  return Array.from(keys);
};

const collectPermissionKeysFromSidebar = (
  nodes: SidebarPermissionNode[],
): string[] => {
  const keys = new Set<string>();

  const traverse = (items?: SidebarPermissionNode[] | null) => {
    if (!items) {
      return;
    }

    items.forEach((item) => {
      if (!item) {
        return;
      }

      if (Array.isArray(item.requiredPermissionKey)) {
        item.requiredPermissionKey.forEach((key) => {
          if (typeof key === "string" && key.trim()) {
            keys.add(key);
          }
        });
      }

      if (typeof item.key === "string" && item.key.trim()) {
        keys.add(item.key);
      }

      traverse(item.children);
    });
  };

  traverse(nodes);

  return Array.from(keys);
};

interface ReportsTestProviderProps {
  children: React.ReactNode;
}

export const ReportsNewProvider: React.FC<ReportsTestProviderProps> = ({
  children,
}) => {
  const dataAdapter = React.useMemo(() => new WebReportingAdapter(), []);
  const userId = useSelector((state) => state.persistedReducer.auth.userId);
  const userTokenInfo = useSelector(
    (state) => state.persistedReducer.auth.userTokenInfo,
  );
  const permissions = useSelector(
    (state) => state.persistedReducer.auth.permissions ?? [],
  );
  const { data: sidebarPermissionsResponse } =
    restApi.useGetSidebarPermissionsQuery({
      ...DEFAULT_API_PAYLOAD,
    });

  const sidebarPermissions = React.useMemo(
    () =>
      (
        (sidebarPermissionsResponse?.result as
          | SidebarPermissionNode[]
          | undefined) ?? []
      ).filter(Boolean),
    [sidebarPermissionsResponse],
  );

  const normalizedPermissions = React.useMemo(() => {
    const sidebarKeys = collectPermissionKeysFromSidebar(sidebarPermissions);
    if (sidebarKeys.length > 0) {
      return sidebarKeys;
    }

    return collectPermissionKeysFromSections(permissions);
  }, [permissions, sidebarPermissions]);

  const authContext = React.useMemo(
    () =>
      new WebReportingAuth(
        () => ({
          id: userId ?? "",
          name:
            `${userTokenInfo?.firstName ?? ""} ${userTokenInfo?.lastName ?? ""}`.trim() ||
            "User",
          email: userTokenInfo?.email,
          accessLevel: String(userTokenInfo?.roles?.[0] ?? "user"),
        }),
        () => normalizedPermissions,
      ),
    [normalizedPermissions, userId, userTokenInfo],
  );

  return (
    <ReportingProvider dataAdapter={dataAdapter} authContext={authContext}>
      {children}
    </ReportingProvider>
  );
};
